import { api } from "@/lib/api";
import type { Issue } from "./issues";

export type TemplateField = {
  key: string;
  label: string;
  type: "text" | "textarea" | "select" | "url";
  required?: boolean;
  options?: string[];
  placeholder?: string;
};

/** Letra ESCALA (E/S/C/A/L/A2). Templates antigos podem ainda ter
 *  valores legacy (analysis/copy/production/optimization) até reseed. */
export type TemplatePhase =
  | "E"
  | "S"
  | "C"
  | "A"
  | "L"
  | "A2"
  | "analysis"
  | "copy"
  | "production"
  | "optimization";
export type TemplateKind = "manual" | "routine";

export type IssueTemplate = {
  id: string;
  key: string;
  title: string;
  description: string | null;
  category: string; // "Conteúdo" | "Anúncios" | "E-mail" | "Automação" | "Web" | "Estratégia" | "Pesquisa"
  defaultAgentId: string | null;
  defaultFormat: "document" | "gallery";
  promptTemplate: string;
  fields: TemplateField[];
  /** Template key da próxima etapa (vira sub-task ao aprovar) */
  nextTemplateKey: string | null;
  /** Hint mostrado ao user no momento da aprovação */
  nextStepHint: string | null;
  /** Se setado, a UI bloqueia o template até integração estar conectada. */
  requiresIntegrationKey: string | null;
  /** Fase do funil — agrupa templates na página /nova-tarefa */
  phase: TemplatePhase;
  /** "manual" aparece em /nova-tarefa, "routine" em /automacoes */
  kind: TemplateKind;
  /** "true" → cadeado de "em breve", não pode ser executado. (string em jsonb) */
  comingSoon: string;
  /** Cron expression pra routines */
  routineCron: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateIssueFromTemplateInput = {
  templateKey: string;
  fieldValues?: Record<string, unknown>;
  projectId?: string | null;
  customTitle?: string | null;
  /** ID do produto do briefing ao qual a tarefa se refere (opcional) */
  productId?: string | null;
  /** IDs de KBs modulares que o user marcou pra anexar nessa task. */
  knowledgeBaseIds?: string[];
  /**
   * Orientação livre que o user dá pra essa tarefa específica — texto curto
   * descrevendo "como ele quer" o entregável. Vai pro topo da description da
   * issue e é injetado pelo PromptBuilder na execução.
   */
  userOrientation?: string | null;
  /**
   * Atalho "já tenho a copy": se preenchido num template que encadeia arte,
   * pula a geração de copy e cria direto o card de arte com este texto.
   */
  pastedCopy?: string | null;
  /**
   * Variações de título: asset da imagem base (da biblioteca de aprovados ou
   * upload) sobre a qual cada headline é aplicada.
   */
  baseImageAssetId?: string | null;
  /**
   * Imagens de referência desta tarefa (foto do sujeito + exemplos de estilo)
   * enviadas na criação. Valem só pra esta tarefa (one-off) — não poluem a
   * biblioteca de marca. Viram `executionState.selectedReferenceAssetIds` e
   * são usadas como reference na geração de arte (o card de arte herda no
   * encadeamento copy → arte).
   */
  selectedReferenceAssetIds?: string[] | null;
  /**
   * Briefing de arte (só pra templates que geram arte). Coletado por múltipla
   * escolha antes de criar. Direciona o roteamento (logo/rosto → Nano), o
   * prompt e o bloqueio (rosto sem foto → pede). Linguagem leiga.
   */
  artBriefing?: ArtBriefing | null;
};

export type ArtBriefing = {
  /** Protagonista da arte — DIRIGE a frente de render (HTML/GPT/Nano).
   *  "texto" → frase de impacto (tipográfico/HTML) · "rosto" → foto real do
   *  expert (Nano) · "pessoa_generica" → pessoa genérica (gerador) ·
   *  "cena" → produto/objeto/ambiente (gerador ou composto). */
  destaque?: string;
  /** "sim" | "nao" */
  logo?: string;
  /** objetivo da peça: "autoridade" | "vender" | "engajar" | "anuncio" | texto livre */
  objetivo?: string;
  /** "fotografico" | "minimalista" | "vibrante" | "ilustrado" | texto livre */
  clima?: string;
  /** LEGADO (substituído por `destaque`; mantido p/ ler issues antigas):
   *  "rosto" | "generica" | "sem" | texto livre */
  pessoa?: string;
  /** o que PRECISA aparecer (texto livre opcional) */
  especifico?: string;
  /** formato do criativo: "feed" (1:1, GPT) | "stories" (9:16, Nano).
   *  Só pra criativo/post de imagem única. */
  formato?: string;
};

export const templatesApi = {
  list: () => api.get<IssueTemplate[]>("/templates"),
  createIssue: (companyId: string, input: CreateIssueFromTemplateInput) =>
    api.post<{ issue: Issue; template: IssueTemplate }>(
      `/companies/${companyId}/issues-from-template`,
      input,
    ),
};
